// AGC: Tour step configurations for the Interactive Guided Tour system (v6.9.0)
// AGC: Each tour is a modular mini-tour that can be played independently

import type { Step } from 'react-joyride'

// AGC: Tour IDs for modular tours
export type TourId = 'navigation' | 'battle' | 'breakdown' | 'features' | 'blockchain'

// AGC: Tour metadata interface
export interface TourMeta {
  id: TourId
  title: string
  description: string
  icon: string
  estimatedTime: string // e.g., "2 min"
  steps: Step[]
}

// AGC: Navigation Tour - Learn the UI layout (v6.9.1 - No hand pointers, improved text)
export const navigationTourSteps: Step[] = [
  {
    target: '[data-tour="top-nav"]',
    content: 'This is your top navigation bar! Access your profile, settings, network, and wallet from here.',
    title: 'Top Navigation Bar',
    placement: 'bottom',
    disableBeacon: true,
    data: { hideHand: true },
  },
  {
    target: '[data-tour="network-button"]',
    content: 'Base Sepolia (testnet) is perfect for testing strategies without risking funds. This is the current network shown here.',
    title: 'Base Sepolia (Testnet)',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="network-server-area"]',
    content: 'Click the network button to see options. Base Mainnet is where real battles with real funds happen. The dropdown will show both networks.',
    title: 'Switch to Base Mainnet',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="server-status"]',
    content: 'Ensure this indicator reads "server online" before you start any battle.',
    title: 'Server Status',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="wallet-button"]',
    content: 'Your wallet shows all your funds: OGB (game tokens for battles), USDC (stablecoin for payments), and ETH (for gas fees). Click to open your NFT inventory where you can view and manage your buffs and potions!',
    title: 'Wallet & Inventory',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="profile"]',
    content: 'View your level, XP, win rate, and battle history.',
    title: 'Player Profile',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="settings"]',
    content: 'Adjust language, tour preferences, and modal behaviors.',
    title: 'Settings',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="bottom-nav"]',
    content: 'Quick access to Home, Games, Market, Quest, and Staking.',
    title: 'Bottom Navigation',
    placement: 'top',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="nav-home"]',
    content: 'Jump back to the home dashboard.',
    title: 'Home Tab',
    placement: 'top',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="nav-games"]',
    content: 'Open the Games section for PvE, PvP, and tournaments.',
    title: 'Games Tab',
    placement: 'top',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="nav-market"]',
    content: 'Purchase buffs and potions in the marketplace. Stakers get 50% discount on all purchases!',
    title: 'Marketplace Tab',
    placement: 'top',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="nav-quest"]',
    content: 'Complete daily, weekly, and monthly quests to earn FarmPro rewards! Daily: 10-50 tokens, Weekly: 30-100 tokens, Monthly: up to 500 tokens!',
    title: 'Quest System',
    placement: 'top',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="nav-staking"]',
    content: 'Stake OGB tokens to earn passive income! How it works: 1) Stake any amount of OGB tokens, 2) Earn weekly revenue share from marketplace sales (distributed as ETH, USDC, and OGB), 3) Get 50% discount on ALL marketplace purchases while staked - this applies automatically to buffs, potions, and all NFT purchases! The more you stake, the more you earn!',
    title: 'Staking & Revenue Share',
    placement: 'top',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="battle-entry"]',
    content: 'This is your battle entry point! First-time players will see an "Claim Airdrop" button to get free OGB tokens and buff NFTs. Players with pending refunds will see a "Claim Refund" button. Once you\'re ready, you\'ll see PLAY PvE (battle AI bots) and PLAY PvP (challenge real players) buttons to start fighting!',
    title: 'Battle Entry',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="tokens-burned"]',
    content: 'Every battle burns OGB tokens permanently! Loser burns 25 OGB, draws burn 50 OGB total. Deflationary economy = increasing value!',
    title: 'Token Burns',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="matchmaking-queue"]',
    content: 'Players waiting for PvP matches appear here. Click "Join Game" to challenge any player directly!',
    title: 'Matchmaking Queue',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="recent-battles"]',
    content: 'View your recent battles: wins, losses, draws, XP gained. Expand to see full history!',
    title: 'Recent Battles',
    placement: 'bottom',
    data: { hideHand: true },
  },
  {
    target: '[data-tour="game-guide"]',
    content: 'Game guide and battle simulator! Practice without wallet - no transactions needed!',
    title: 'Game Guide & Simulator',
    placement: 'bottom',
    data: { hideHand: true },
  },
]

// AGC: Battle Tour - Learn game modes on Games page (v6.9.0)
export const battleTourSteps: Step[] = [
  {
    target: '[data-tour="battle-quick-battle"]',
    content: 'Quick Battle (PvE) lets you battle AI bots instantly! Perfect for practice, earning XP, and testing strategies. Entry cost: 50 OGB. Win to earn 85 OGB (70% ROI)!',
    title: 'Quick Battle (PvE)',
    placement: 'bottom',
    disableBeacon: true,
    data: {
      hideHand: true,
    },
  },
  {
    target: '[data-tour="battle-player-vs-player"]',
    content: 'Player vs Player (PvP) matches you against real opponents! Queue against players of similar level. Entry cost: 50 OGB. Winner takes 85 OGB from the pool!',
    title: 'Player vs Player (PvP)',
    placement: 'bottom',
    data: {
      hideHand: true,
    },
  },
  {
    target: '[data-tour="battle-tournaments"]',
    content: 'Join round-robin tournaments! Compete against multiple players in structured brackets. Entry: 50 OGB per player. Top 3 finishers win prizes: 1st place gets the largest share, 2nd and 3rd place also receive rewards. All prizes are distributed on-chain and verifiable on BaseScan!',
    title: 'Tournaments',
    placement: 'bottom',
    data: {
      hideHand: true,
    },
  },
  {
    target: '[data-tour="battle-private-room"]',
    content: 'Private Room (Coming Soon) - Play versus friends without tokens or quest requirements! Invite friends to battle in private matches. Perfect for practice and friendly competition. No OGB entry needed, and battles won\'t count toward quests.',
    title: 'Private Room',
    placement: 'bottom',
    data: {
      hideHand: true,
    },
  },
  {
    target: '[data-tour="battle-quick-battle"]',
    content: 'Click here to start a Quick Battle! This will take you to the PvE battle setup page where you can select difficulty, buffs, and start fighting AI bots.',
    title: 'Start Quick Battle',
    placement: 'bottom',
    data: {
      hideHand: true, // AGC: Step 5 - no hand pointer
    },
  },
  {
    target: 'body', // AGC: Use body as target since we navigate to a different page
    content: 'Here you can see your current level and XP progress bar. The bar shows how much XP you need to reach the next level. Win battles to earn XP and level up, which increases your attack and defense stats!',
    title: 'Player Progression',
    placement: 'center',
    data: {
      hideHand: true, // AGC: Step 6 - no hand
      navigateTo: 'quickBattle', // AGC: Navigate to quickBattle when this step starts
      highlightTarget: '[data-tour="battle-player-progression"]', // AGC: Manually highlight this after navigation
    },
  },
  {
    target: '[data-tour="battle-difficulty-normal"]',
    content: 'Select the difficulty level for your battle! Normal: Bots +3 to +5 levels. Expert: Bots +7 to +10 levels. Insane: Bots +13 to +20 levels. Higher difficulty = stronger bots = more XP if you win, but harder to beat!',
    title: 'Difficulty Selector',
    placement: 'bottom',
    data: {
      hideHand: true, // AGC: Step 7 - no hand pointer
      simulateClick: true,
      clickTarget: '[data-tour="battle-difficulty-normal"]',
    },
  },
  {
    target: '[data-tour="battle-buff-selector"]',
    content: 'This is your Buff & Potion selector! You can buy buffs and potions in the marketplace. The airdrop gives you some buff NFTs. Pro tip: Stake 500 FARMPRO and you get 50% discount on marketplace!',
    title: 'Buff & Potion Selector',
    placement: 'bottom',
    data: {
      hideHand: true, // AGC: Step 8 - no hand pointer
    },
  },
  {
    target: '[data-tour="battle-category-tabs"]',
    content: 'Here you select the category of NFTs. CLASSIC: Current buffs (Shield, Pierce, Haste, Frost). POTIONS: Healing, Mana, Double Strike, and Poison potions. FRIENDS: Coming soon - invite friends to join your battles!',
    title: 'Category Selection',
    placement: 'bottom',
    data: {
      hideHand: true, // AGC: Step 9 - no hand
    },
  },
  {
    target: '[data-tour="battle-buff-panels"]',
    content: 'You can select up to 2 buffs for battle! Each buff has different effects. If you select 2 buffs, the 2nd buff gets more chance to activate if the 1st buff fails. This improves your chances!',
    title: 'Select Buffs (Max 2)',
    placement: 'bottom',
    data: {
      hideHand: true, // AGC: Step 10 - no hand
    },
  },
  {
    target: '[data-tour="battle-buff-first"]',
    content: 'Click on any buff to select it! You can select up to 2 buffs. Each buff has different effects: Shield gives you 25% extra defense (reduces incoming damage by 25%), Pierce increases your damage by 25%, Haste adds +5 damage when activated, and Frost freezes your opponent for one turn (sets their damage to 0).',
    title: 'Select a Buff',
    placement: 'bottom',
    data: {
      hideHand: true, // AGC: Step 11 - no hand (removed repeatHand animation)
    },
  },
  {
    target: '[data-tour="battle-buff-selector"]',
    content: 'Click on the POTIONS tab to see all available potions! Potions are grouped by type and rarity. You\'ll see Healing (red), Mana (blue), Double Strike (purple), and Poison (green) potions, each with different rarities: Common (white), Uncommon (green), Rare (blue), Epic (purple), and Legendary (orange).',
    title: 'Potion Selection',
    placement: 'bottom',
    data: {
      hideHand: true, // AGC: Step 12 - no hand
      simulateClick: 'potions-tab',
      navigateTo: 'potions-area',
    },
  },
  {
    target: '[data-tour="battle-potion-top-left"]',
    content: 'You can select only 1 potion for battle! Click on it to select. Each potion has different effects: Healing restores energy points, Mana increases buff effect, Double Strike increases damage if turn is won, and Poison deals damage for 5 turns.',
    title: 'Select a Potion',
    placement: 'bottom',
    data: {
      hideHand: true, // AGC: Step 13 - no hand, just cutout and explanation
    },
  },
  {
    target: 'body', // AGC: Use body as target since button might not be visible/available
    content: 'Click "Start Battle" to begin! You\'ll need to approve 3 transactions: 1) Approve OGB token contract, 2) Approve buff contract, 3) Approve potion contract. Then 1 transaction will take out 50 OGB tokens, burn your selected buffs, and use your selected potion.',
    title: 'Start Battle',
    placement: 'center',
    data: {
      hideHand: true, // AGC: Step 14 - no hand (just explanation)
      highlightTarget: '[data-tour="battle-start-button"]', // AGC: Manually highlight the button
    },
  },
  {
    target: 'body',
    content: 'Choose your battle mode! PLAY PvE: Real on-chain battle with transactions. Play Simulation: Practice battle without wallet or transactions.',
    title: 'Battle Mode Selection',
    placement: 'center',
    data: {
      showModeSelection: true,
      showSimulationButton: true, // AGC: Step 15 - Show "Play Simulation" button in tooltip
      hideHand: true, // AGC: No hand pointer
    },
  },
  // AGC: Step 16 (index 15) - Navigate to simulation page and explain info panel
  {
    target: '[data-tour="simulation-info-panel"]',
    content: 'This is a simulation battle! No transactions will be made on-chain, and no wallet connection is needed. Perfect for testing strategies and learning the battle system without spending tokens.',
    title: 'Simulation Mode',
    placement: 'bottom',
    data: {
      navigateTo: 'newSimulation', // AGC: Navigate to newSimulation page
      hideHand: true, // AGC: No hand pointer
      scrollToTop: true, // AGC: Scroll to top when entering this step
    },
  },
  // AGC: Step 16 (index 16) - Explain buffs and ask to select 2
  {
    target: '[data-tour="simulation-buff-area"]',
    content: 'Select up to 2 buffs for your battle! Each buff has different effects: Shield gives 25% extra defense, Pierce increases damage by 25%, Haste adds +5 damage when activated, and Frost freezes your opponent for one turn. Buffs depend on luck to activate - the more buffs you select, the better your chances!',
    title: 'Select Buffs (Max 2)',
    placement: 'bottom',
    data: {
      hideHand: true,
      waitForBuffsSelected: 2, // AGC: Wait for 2 buffs to be selected before advancing
      scrollToTop: true, // AGC: Scroll to top like navigation tour
    },
  },
  // AGC: Step 18 (index 17) - Ask to click potions tab
  {
    target: '[data-tour="battle-potions-tab"]',
    content: 'Click on the POTIONS tab to see available potions! Potions work differently from buffs - they apply directly when used, with no luck involved.',
    title: 'Open Potions Tab',
    placement: 'bottom',
    data: {
      simulateClick: true,
      clickTarget: '[data-tour="battle-potions-tab"]',
      waitForTabSwitch: true, // AGC: Wait for potions tab to be clicked
      scrollToTop: true, // AGC: Scroll to top like navigation tour
      allowNextWithoutAction: true, // AGC: Allow clicking Next even if tab not clicked
    },
  },
  // AGC: Step 19 (index 18) - Explain potions in potions area
  {
    target: '[data-tour="battle-potions-area"]',
    content: 'Here you can activate or deactivate a potion, or choose which turn you want to use it. Mana potions are for all turns and last 5 turns. Healing and Double Strike are one turn only. Select a potion (optional) and click Next to continue!',
    title: 'Potions Area',
    placement: 'bottom',
    data: {
      hideHand: true,
      // AGC: Do NOT auto-advance - user clicks Next to proceed
      // AGC: This prevents timing issues when advancing to step 20
    },
  },
  // AGC: Step 20 (index 19) - Explain start button and ask to click
  {
    target: '[data-tour="simulation-start-button"]',
    content: 'Click "Start Simulation Battle" to begin! Remember: this is a simulation - no transactions will be made on-chain, and no tokens will be spent. Perfect for practice!',
    title: 'Start Simulation Battle',
    placement: 'top',
    data: {
      hideHand: true,
      // AGC: User clicks the start button or clicks Next to proceed
    },
  },
  // AGC: Step 21 (index 20) - Explain turn and timer
  {
    target: '[data-tour="simulation-turn-timer"]',
    content: 'You have 60 seconds to choose your hand for each turn! In simulation mode, auto-submit is disabled so you can take your time learning. In real battles, the game will auto-play if time runs out.',
    title: 'Turn Timer',
    placement: 'bottom',
    data: {
      hideHand: true,
      waitForBattleStart: true, // AGC: Wait for battle to actually start before showing this step
    },
  },
  // AGC: Step 21 (index 21) - Explain player energy area
  {
    target: '[data-tour="simulation-player-energy"]',
    content: 'This area shows your player information! You can see your level, win rate, selected buffs, and energy bar. The energy bar shows how much energy you have left - when it reaches 0, you lose!',
    title: 'Player Information',
    placement: 'bottom',
    data: {
      hideHand: true,
    },
  },
  // AGC: Step 22 (index 22) - Explain potion activation
  {
    target: '[data-tour="simulation-potion-button"]',
    content: 'Here you can activate or deactivate your potion, or choose which turn to use it! Mana potion is active for all turns. Poison potion lasts 5 turns. Healing and Double Strike are one turn only. Click the potion button to toggle it on/off for the current turn.',
    title: 'Potion Activation',
    placement: 'right',
    data: {
      hideHand: true,
    },
  },
  // AGC: Step 24 (index 23) - Explain hero selection
  {
    target: '[data-tour="simulation-hero-selection"]',
    content: 'This is a carousel-style selection! Swipe or click to choose: Paper > Rock > Scissors > Paper (circular). Win deals +30 damage, lose deals +10 damage, and draw deals +15 damage. Choose wisely!',
    title: 'Select Hero (Origami)',
    placement: 'top', // AGC: Tooltip at top of hero selection
    data: {
      hideHand: true,
    },
  },
  // AGC: Step 25 (index 24) - Explain element selection
  {
    target: '[data-tour="simulation-element-selection"]',
    content: 'This is also a carousel-style selection! Swipe or click to choose: Fire > Ice > Wind > Water > Fire (circular). Win deals +10 damage to attacker and -5 damage to defender. Draw deals 0 damage. Neutral (example: Water vs Ice) deals both -5 damage.',
    title: 'Select Element',
    placement: 'bottom', // AGC: Tooltip at bottom of element selection
    data: {
      hideHand: true,
    },
  },
  // AGC: Step 26 (index 25) - Explain submit turn button
  {
    target: '[data-tour="simulation-submit-turn"]',
    content: 'Click "Submit Turn" to lock in your choices! Both players have 60 seconds from the start of each turn. Sometimes you need to wait if your opponent is slower. The timer shows how much time is left.',
    title: 'Submit Turn',
    placement: 'top',
    data: {
      hideHand: true,
      allowClickToAdvance: true, // AGC: Clicking submit button advances tour
    },
  },
  // AGC: Step 27 (index 26) - Explain your card (full card from BattleTurnModal)
  {
    target: '[data-tour="simulation-your-card-full"]',
    content: 'This is YOUR CARD showing all your turn information! It displays your hero choice, element, damage dealt, energy level, buffs, rarity bonuses, and the complete damage breakdown. This card summarizes everything about your attack this turn.',
    title: 'Your Turn Card',
    placement: 'right',
    data: {
      hideHand: true,
      advanceOnClick: true, // AGC: Clicking card advances tour
    },
  },
  // AGC: Step 28 (index 27) - Explain opponent card (full card from BattleTurnModal)
  {
    target: '[data-tour="simulation-opponent-card-full"]',
    content: 'Opponent damage, buffs & breakdown. Analyze their strategy!',
    title: 'Opponent Card',
    placement: 'top-start',
    data: {
      hideHand: true,
      scrollToTop: true, // AGC: Scroll to show tooltip at top
    },
  },
  // AGC: Step 29 (index 28) - Explain breakdown area (full breakdown from YOU card)
  {
    target: '[data-tour="simulation-breakdown-area-full"]',
    content: 'This is the DAMAGE BREAKDOWN showing the game engine formula! HERO (win/lose/draw) + LEVEL + ELEMENT (advantage) + RARITY bonus + BUFF effect + POTION effect - OPPONENT DEFENSE = Final Damage. Master this to win!',
    title: 'Your Damage Breakdown',
    placement: 'top',
    data: {
      hideHand: true,
    },
  },
  // AGC: Step 30 (index 29) - Explain close/continue buttons with cutouts
  {
    target: 'body',
    content: 'You can close this modal to choose another turn, or click Continue to proceed. Remember: you have 60 seconds from the moment you enter this turn result page. GOOD LUCK IN YOUR BATTLES! 🎮',
    title: '🎯 Turn Result Navigation',
    placement: 'center',
    data: {
      hideHand: true,
      highlightCloseButton: true, // AGC: Add cutout around close button
      highlightContinueButton: true, // AGC: Add cutout around continue button
    },
  },
]

// AGC: Battle Breakdown Tour - Understand damage calculation
export const breakdownTourSteps: Step[] = [
  {
    target: '[data-tour="damage-formula"]',
    content: 'Damage = Base + Level + Element + Rarity + Buffs. Master this formula to win!',
    title: 'Damage Formula',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="element-cycle"]',
    content: 'Element advantage gives +10 damage, disadvantage gives -5. Learn the cycle!',
    title: 'Element Cycle',
    placement: 'right',
  },
  {
    target: '[data-tour="rarity-system"]',
    content: 'Each turn has a chance for rare attacks: Diamond (+50), Gold (+25), Silver (+10), Bronze (+5).',
    title: 'Rarity System',
    placement: 'left',
  },
  {
    target: '[data-tour="buff-effects"]',
    content: 'Shield blocks 25% damage. Pierce boosts attack 25%. Haste adds +5 damage. Frost freezes opponent!',
    title: 'Buff Effects',
    placement: 'bottom',
  },
  {
    target: '[data-tour="turn-breakdown"]',
    content: 'After each turn, review the damage breakdown to understand what happened.',
    title: 'Turn Breakdown',
    placement: 'top',
  },
]

// AGC: Features Tour - Explore game features
export const featuresTourSteps: Step[] = [
  {
    target: '[data-tour="marketplace"]',
    content: 'Buy NFT buffs and potions with FarmPro, ETH, or USDC. Prices increase as more are minted!',
    title: 'NFT Marketplace',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="inventory"]',
    content: 'View all your owned buffs and potions. Plan your strategy before battle!',
    title: 'Your Inventory',
    placement: 'bottom',
  },
  {
    target: '[data-tour="staking"]',
    content: 'Stake 500+ FarmPro to unlock quests and get 50% marketplace discount. Earn weekly revenue share!',
    title: 'Staking Rewards',
    placement: 'bottom',
  },
  {
    target: '[data-tour="quests"]',
    content: 'Complete daily, weekly, and monthly quests to earn FarmPro rewards (10-500 tokens per quest)!',
    title: 'Quest System',
    placement: 'bottom',
  },
  {
    target: '[data-tour="tournaments"]',
    content: 'Join round-robin tournaments! Top 3 win prizes: 150, 100, and 50 FarmPro.',
    title: 'Tournaments',
    placement: 'bottom',
  },
  {
    target: '[data-tour="profile"]',
    content: 'Track your level, XP, win rate, and battle history. Level up to increase attack and defense!',
    title: 'Player Profile',
    placement: 'left',
  },
]

// AGC: Blockchain Tour - Understand on-chain features
export const blockchainTourSteps: Step[] = [
  {
    target: '[data-tour="wallet-status"]',
    content: 'Your wallet connects to Base network (Coinbase L2). Low fees enable micro-transactions!',
    title: 'Base Network',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="onchain-battles"]',
    content: 'All battle outcomes are recorded on-chain. Verify any battle on BaseScan!',
    title: 'On-Chain Battles',
    placement: 'bottom',
  },
  {
    target: '[data-tour="chainlink-vrf"]',
    content: 'Chainlink VRF provides provably fair randomness. Rarity and buff activations are tamper-proof!',
    title: 'Fair Randomness',
    placement: 'bottom',
  },
  {
    target: '[data-tour="token-burns"]',
    content: '15% of battle fees are burned permanently (15 FarmPro per battle). Deflationary token economics!',
    title: 'Token Burns',
    placement: 'bottom',
  },
  {
    target: '[data-tour="revenue-share"]',
    content: 'Stakers earn weekly revenue share from marketplace sales in ETH, USDC, and FarmPro!',
    title: 'Revenue Share',
    placement: 'bottom',
  },
]

// AGC: All tours metadata for tour selection
export const allTours: TourMeta[] = [
  {
    id: 'navigation',
    title: 'Navigation Tour',
    description: 'Learn the UI layout and find your way around',
    icon: '🧭',
    estimatedTime: '1 min',
    steps: navigationTourSteps,
  },
  {
    id: 'battle',
    title: 'Battle Tour',
    description: 'Master the battle system and start winning',
    icon: '⚔️',
    estimatedTime: '2 min',
    steps: battleTourSteps,
  },
  {
    id: 'blockchain',
    title: 'Blockchain Tour',
    description: 'Discover on-chain features and token economics',
    icon: '🔗',
    estimatedTime: '1 min',
    steps: blockchainTourSteps,
  },
  {
    id: 'breakdown',
    title: 'Battle Breakdown',
    description: 'Understand damage calculation and strategy',
    icon: '📊',
    estimatedTime: '1 min',
    steps: breakdownTourSteps,
  },
  {
    id: 'features',
    title: 'Features Tour',
    description: 'Explore marketplace, staking, quests & more',
    icon: '✨',
    estimatedTime: '2 min',
    steps: featuresTourSteps,
  },
]

// AGC: Get tour by ID
export const getTourById = (id: TourId): TourMeta | undefined => {
  return allTours.find((tour) => tour.id === id)
}

// AGC: Get all steps for a combined full tour
export const getFullTourSteps = (): Step[] => {
  return [
    ...navigationTourSteps,
    ...battleTourSteps,
    ...breakdownTourSteps,
    ...featuresTourSteps,
    ...blockchainTourSteps,
  ]
}

